import React, { FC, lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'

import PrivateRoute from '../common/guards/private.route'
import PublicRoute from '../common/guards/public.route'
import { RouteEnum } from '../constants/routes.enum'
import { AuthLayout } from '../layouts/auth/auth.layout'
import { MainLayoutNoPadding } from '../layouts/main/main-layout-no-padding'
import { MainLayout } from '../layouts/main/main.layout'
import { LoginPage } from '../modules/auth/login/login.page'
import { OnboardingStepperPage } from '../modules/auth/onboarding-stepper/onboarding-stepper.page'
import { ConfirmEmail } from '../modules/auth/register-page/confirm-email/confirm-email'
import { Register } from '../modules/auth/register-page/register.page'
import { ObjectsPage } from '../modules/objects/objects/objects.page'
import { RolesPage } from '../modules/roles/roles/roles.page'
import { SettingsPage } from '../modules/settings/settings/settings.page'
import { TasksPage } from '../modules/tasks/tasks.page'
import { UsersPage } from '../modules/users/users/users.page'
import { MilestonePage } from '../modules/workspace/milestone/milestone.page'
import { MilestoneAddPage } from '../modules/workspace/milestone-add/milestone-add.page'
import { MilestoneUpdatePage } from '../modules/workspace/milestone-add/milestone-update.page'
import { MilestoneSequencePage } from '../modules/workspace/milestone-sequence/milestone-sequence.page'
import { MilestoneTransferPage } from '../modules/workspace/milestone-transfer/milestone-transfer.page'
import { WorkspacePage } from '../modules/workspace/workspace/workspace.page'

// Lazy-loaded modules
const AuthModule = lazy(() => import('../modules/auth/auth.module'))
const WorkspaceModule = lazy(() => import('../modules/workspace/workspace.module'))
const UsersModule = lazy(() => import('../modules/users/users.module'))
const ObjectsModule = lazy(() => import('../modules/objects/objects.module'))
const RolesModule = lazy(() => import('../modules/roles/roles.module'))
const SettingsModule = lazy(() => import('../modules/settings/settings.module'))

export const AppRoutes: FC = () => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Routes>
        {/* PUBLIC ROUTES */}
        <Route element={<PublicRoute />}>
          <Route
            element={<AuthLayout />}
            path={RouteEnum.AUTH}
          >
            <Route element={<AuthModule />}>
              <Route
                element={<LoginPage />}
                index
              />
              <Route
                element={<Register />}
                path={`${RouteEnum.REGISTER}/:token?`}
              />
              <Route
                element={<ConfirmEmail />}
                path={RouteEnum.CONFIRM_EMAIL}
              />
            </Route>
          </Route>
        </Route>

        {/* PRIVATE ROUTES */}
        <Route element={<PrivateRoute />}>
          {/* Redirect from main to workspace */}
          <Route
            element={
              <Navigate
                to={RouteEnum.WORKSPACE}
                replace
              />
            }
            path={RouteEnum.MAIN}
          />

          {/* Main Layout */}
          <Route
            element={<MainLayout />}
            path={RouteEnum.MAIN}
          >
            <Route
              element={<OnboardingStepperPage />}
              path={RouteEnum.ONBOARDING}
            />

            {/* Users */}
            <Route
              element={<UsersModule />}
              path={RouteEnum.USERS}
            >
              <Route
                element={<UsersPage />}
                index
              />
            </Route>

            {/* Roles */}
            <Route
              element={<RolesModule />}
              path={RouteEnum.ROLES}
            >
              <Route
                element={<RolesPage />}
                index
              />
            </Route>

            {/* Objects */}
            <Route
              element={<ObjectsModule />}
              path={RouteEnum.OBJECTS}
            >
              <Route
                element={<ObjectsPage />}
                index
              />
            </Route>

            {/* Tasks */}
            <Route
              element={<TasksPage />}
              path={RouteEnum.TASKS}
            />

            {/* Workspace */}
            <Route
              element={<WorkspaceModule />}
              path={RouteEnum.WORKSPACE}
            >
              <Route
                element={<MilestonePage />}
                path={RouteEnum.MILESTONES}
              />
              <Route
                element={<MilestoneTransferPage />}
                path={RouteEnum.MILESTONE_TRANSFER}
              />
            </Route>

            {/* Settings */}
            <Route
              element={<SettingsModule />}
              path={RouteEnum.SETTINGS}
            >
              <Route
                element={<SettingsPage />}
                index
              />
            </Route>
          </Route>

          {/* Main Layout without padding */}
          <Route
            element={<MainLayoutNoPadding />}
            path={RouteEnum.MAIN}
          >
            <Route
              element={<WorkspaceModule />}
              path={RouteEnum.WORKSPACE}
            >
              <Route
                element={<WorkspacePage />}
                index
              />
              <Route
                element={<MilestoneAddPage />}
                path={`${RouteEnum.MILESTONES}/${RouteEnum.ADD}`}
              />
              <Route
                element={<MilestoneUpdatePage />}
                path={`${RouteEnum.MILESTONES}/:id`}
              />
              <Route
                element={<MilestoneSequencePage />}
                path={`${RouteEnum.MILESTONE_SEQ}`}
              />
            </Route>
          </Route>
        </Route>
      </Routes>
    </Suspense>
  )
}
